import { eventTypeLabels, type EventType as EventTypeName } from '@greasemap/validation';
import type { EventDetail, EventSummary, EventType } from './types';

/**
 * A human label for an event type.
 *
 * @param type - The event type.
 * @returns The display label, falling back to the raw value for an unknown type.
 */
export function eventTypeLabel(type: EventType): string {
  return eventTypeLabels[type as EventTypeName] ?? type;
}

/**
 * Formats when an event happens as one line.
 *
 * @param event - The event, needing at least a start.
 * @returns A readable date, with a time range when an end is known.
 */
export function formatEventWhen(event: Pick<EventSummary, 'startsAt' | 'endsAt'>): string {
  const start = new Date(event.startsAt);
  const date = start.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const startTime = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  if (event.endsAt === null || event.endsAt === undefined) {
    return `${date} · ${startTime}`;
  }

  const endTime = new Date(event.endsAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${date} · ${startTime} – ${endTime}`;
}

/**
 * Describes how trustworthy a listing's freshness is, for the confirm signal.
 *
 * The distinct-confirmer count is surfaced rather than only the timestamp, because a listing many
 * different people confirmed is more credible than one an owner bumped alone.
 *
 * @param event - The event detail.
 * @returns A short sentence describing the confirmation state.
 */
export function confirmationLabel(
  event: Pick<EventDetail, 'lastConfirmedAt' | 'confirmerCountThisWeek' | 'isStale'>,
): string {
  if (event.lastConfirmedAt === null || event.lastConfirmedAt === undefined) {
    return 'Not confirmed yet';
  }

  const when = new Date(event.lastConfirmedAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  const count = event.confirmerCountThisWeek;

  if (count > 0) {
    const people = count === 1 ? 'person' : 'people';
    return `Confirmed by ${count} ${people} this week`;
  }

  return `Last confirmed ${when}`;
}

/**
 * Formats a distance for display.
 *
 * @param distanceKm - The distance in kilometres, or null when the result did not come from a search.
 * @returns A short distance string, or an empty string when there is nothing to show.
 */
export function formatDistance(distanceKm: number | null | undefined): string {
  if (distanceKm === null || distanceKm === undefined) {
    return '';
  }

  return distanceKm < 10 ? `${distanceKm.toFixed(1)} km` : `${Math.round(distanceKm)} km`;
}
